<template>
  <div class="universe-page">
    <!-- 全局头部导航栏 -->
    <header-nav></header-nav>

    <div ref="scene" class="universe-scene">
      <!-- 宇宙画布：星云 / 星辰 / 时间长河 / 星座 / 粒子 -->
      <canvas ref="canvas" class="u-canvas"></canvas>

      <!-- 鼠标光晕 -->
      <div ref="cursor" class="u-cursor"></div>

      <!-- ============ 阶段一：时空之门 ============ -->
      <transition name="gate-out">
        <div v-if="stage === 'gate'" class="gate-stage">
          <div class="gate-wrap">
            <!-- 门框（拱形） -->
            <div class="gate-frame">
              <div class="frame-gem"></div>
              <div class="frame-archline"></div>
            </div>

            <!-- 左门扇 -->
            <div class="door-panel panel-left" :class="{ 'panel-open': doorOpen }">
              <div class="panel-dots"></div>
              <span class="p-rune r1">时</span>
              <span class="p-rune r2">之</span>
              <span class="p-rune r3">钟</span>
            </div>
            <!-- 右门扇 -->
            <div class="door-panel panel-right" :class="{ 'panel-open': doorOpen }">
              <div class="panel-dots"></div>
              <span class="p-rune r1">镇</span>
              <span class="p-rune r2">星</span>
              <span class="p-rune r3">辰</span>
            </div>

            <!-- 门缝光 -->
            <div class="door-seam" :class="{ 'seam-off': doorOpen }"></div>

            <!-- 封印法阵 -->
            <div
              ref="seal"
              class="seal-array"
              :class="{ breaking: arrayBreaking }"
              @click="openGate"
            >
              <div class="array-halo"></div>
              <div class="array-ring ar1"></div>
              <div class="array-ring ar2"></div>
              <div class="array-ring ar3"></div>
              <div class="array-oct a-oct1"></div>
              <div class="array-oct a-oct2"></div>
              <div class="array-ticks"></div>
              <div class="array-core"></div>
              <div class="array-rune-wrap">
                <span
                  v-for="(r, i) in portalRunes"
                  :key="'a' + i"
                  class="array-rune"
                  :style="runeStyle(i, 86)"
                >{{ r }}</span>
              </div>
<!--              <div class="array-hint">点击法阵 · 解除封印</div>-->
            </div>

            <!-- 门标题 -->
            <div class="gate-title">星 骸 之 墟</div>
          </div>
        </div>
      </transition>

      <!-- ============ 阶段二：宇宙幻境 ============ -->
      <transition name="cosmos-in">
        <div v-if="stage === 'cosmos'" class="cosmos">
          <!-- 罗盘时钟（纯文字 · 机械级联：外圈秒 → 内圈年） -->
          <div class="compass-clock" :style="clockStyle()">
            <div class="clock-aura"></div>
            <div class="clock-bob">
              <!-- 菱形金色花纹罗盘背景（用户素材旋转45°抠图） -->
              <div class="compass-frame" :style="frameStyle()"></div>
              <div class="clock-outer-ring"></div>
              <!-- 最外圈：星座符号环（匀速顺时针旋转） -->
              <div class="zodiac-ring">
                <span
                  v-for="(sym, i) in zodiacSymbols"
                  :key="'z' + i"
                  class="zodiac-sym"
                  :style="zodiacStyle(i)"
                >{{ sym }}</span>
              </div>
              <div class="clock-ticks"></div>
              <div class="clock-sheen"></div>
              <div
                v-for="(ring, ri) in clockRings"
                :key="'ring' + ri"
                ref="clockRing"
                class="clock-ring"
                :class="{ step: ring.step && clockReady, sec: ring.sec && clockReady }"
              >
                <span
                  v-for="(t, ti) in ring.items"
                  :key="'t' + ri + '-' + ti"
                  class="ring-item"
                  :style="ringItemStyle(ring, ti)"
                >{{ t }}</span>
              </div>
              <!-- 当前时间读取框（右半边中间水平位置） -->
              <div class="clock-readout">
<!--                <span class="clock-readout-label">此刻</span>-->
              </div>
              <!-- 中央：神秘之门（时间长河汇聚之地） -->
              <div class="clock-center-zone">
                <div class="portal-inner" :style="portalInnerStyle()"
                  @click="onPortalClick"
                >
                  <div class="portal-aura"></div>
                  <div class="portal-ring pr1"></div>
                  <div class="portal-ring pr2"></div>
                  <div class="portal-swirl"></div>
                  <div class="portal-core"></div>
                  <div class="portal-rune-wrap">
                    <span
                      v-for="(r, i) in portalRunes"
                      :key="'p' + i"
                      class="portal-rune"
                      :style="runeStyle(i, clockSize * 0.11)"
                    >{{ r }}</span>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <!-- 文字 UI -->
          <div class="cosmos-ui">
            <div class="cosmos-title">
              <h2>欢迎 · 宇宙</h2>
              <p>星辰为引 · 时间为河 · 万物终将归于星辰</p>
            </div>
            <div class = "wenzi">
              <!-- 歌词式滚动轮播：5 段可见，从下往上逐段滚动 -->
              <div class="wenzi-window" :class="{ 'lyric-scrolling': lyricScrolling }">
                <!-- 中心光晕：行滚过中间时被照亮 -->
                <div class="lyric-glow"></div>
                <div
                  ref="track"
                  class="wenzi-track"
                  :style="{ transform: 'translateY(' + lyricOffset + 'px)' }"
                >
                  <p
                    v-for="(line, i) in showLines"
                    :key="'wl' + i"
                    class="wenzi-line"
                    :class="{ 'lyric-active': i === 2 }"
                  >
                    <!-- 主唱行（窗口中间）拆成单字，卡拉OK逐字点亮 -->
                    <template v-if="i === 2">
                      <span
                        v-for="(ch, ci) in line.split('')"
                        :key="'c' + ci"
                        class="lyric-ch"
                        :class="{ lit: ci < lyricStep }"
                      >{{ ch }}</span>
                    </template>
                    <template v-else>{{ line }}</template>
                  </p>
                </div>
              </div>
              <!-- 左侧流动时间轴 -->
              <div class="lyric-rail">
                <span class="rail-dot"></span>
              </div>
              <!-- 微光星星点缀 -->
              <span class="lyric-star s1"></span>
              <span class="lyric-star s2"></span>
              <span class="lyric-star s3"></span>
              <span class="lyric-star s4"></span>
            </div>


            <div class="cosmos-hint">✦ 移动鼠标 · 群星为您让路 ✦</div>
          </div>
        </div>
      </transition>

      <!-- 开门闪光 -->
      <div class="flash" :class="{ on: flashOn }"></div>
    </div>
  </div>
</template>

<script>
import HeaderNav from '../HeaderNav.vue'

const TAU = Math.PI * 2

// 歌词轮播：每行固定高度（两行文字），窗口高度 = 5 行
const LYRIC_LINE_H = 60
const LYRIC_WINDOW_LINES = 5
const LYRIC_MOVE_MS = 1000
// 卡拉OK逐字点亮：每字点亮间隔（所有行统一速度）、点亮完成后的停留时长
const LYRIC_CHAR_MS = 120
const LYRIC_PAUSE_MS = 600

// 歌词内容（散文诗）
const LYRICS = [
  '夜',
  '铺开无垠的墨蓝画布，星辰是散落的碎钻，',
  '每一颗都在诉说着亿万年的孤独。',
  '我站在穹顶之下，',
  '仰望这场永不落幕的光之盛宴，忽然觉得自己轻如尘埃。',
  '是的，尘埃。',
  '宇宙浩瀚，星辰璀璨，我们都不过是宇宙中的一粒尘埃。',
  '这听起来有些渺小，有些悲凉。',
  '可你是否想过——我们身体里的每一个原子，都曾在某颗遥远恒星的核心深处，',
  '被锻造、被淬炼，然后在某场壮丽的超新星爆发中，被抛向虚空，',
  '穿越无尽的光年，最终汇聚成此刻能呼吸、能思考、能仰望的你和我。',
  '我们确实是尘埃，却是会发光的尘埃。是宇宙漫长岁月里，偶然凝聚、必然相遇的奇迹。',
  '此刻吹过耳畔的风，是百亿年前宇宙大爆炸的余温；',
  '脚下沉默的泥土，藏着无数星辰的遗骸。我们饮下的水，呼吸的空气，皮肤上掠过的光，无一不是宇宙的馈赠。',
  '我们从来都不是旁观者——我们就是宇宙本身，在短暂地、深情地凝视自己。',
  '想到这里，忽然觉得“渺小”这个词失去了重量。',
  '一粒尘埃如果懂得自己是星河的一部分，那么它便拥有了整条星河的心跳。',
  '于是，我珍惜这个瞬间。',
  '风拂过睫毛的瞬间，星光落进瞳孔的瞬间，心脏在胸腔里怦然跳动的瞬间。',
  '每一个瞬间，都是宇宙酝酿了百亿年才得以绽放的花。',
  '时间是一条河，我们都是涉水而行的人。',
  '从星辰中来，终将回到星辰中去。',
  '可这途中相遇的光，彼此温暖的刹那，擦肩时交换的微笑，便是这趟旅途全部的意义。',
  '宇宙浩瀚，星辰璀璨。而你是尘埃，是奇迹，是宇宙写给自己的、最温柔的情书。',
  '万物终将归于星辰。但在此之前，让我们热烈地、温柔地、无怨无悔地，活着。',
  '宇宙，是星辰的海洋，是时间的河流，是万物的家园。',
  '宇宙浩瀚，星辰璀璨，我们都是宇宙中的一粒尘埃。',
  '然而，正是这粒尘埃，构成了我们独特的存在。',
  '我们每个人都是宇宙的一部分，都是宇宙的奇迹。',
  '让我们珍惜每一个瞬间，珍惜每一个存在，珍惜每一个奇迹。',
  '因为，这就是我们的宇宙，这就是我们的生命。',
  '—— 星辰'
]

const STAR_COLORS = ['#ffffff', '#ffffff', '#ffe9f5', '#e8f4ff', '#fff3d6', '#f3d9ff', '#c9f4ff']
const PARTICLE_SPRITES = ['pink', 'cyan', 'gold', 'purple']

// 星座定义（归一化坐标）
const CONSTELLATIONS = [
  {
    name: '猎户座',
    shape: [[0.5, 0], [0.5, 0.22], [0.5, 0.45], [0.33, 0.62], [0.67, 0.62], [0.28, 0.86], [0.72, 0.86]],
    links: [[0, 1], [1, 2], [2, 3], [2, 4], [3, 5], [4, 6]]
  },
  {
    name: '北斗七星',
    shape: [[0, 0.08], [0.2, 0.18], [0.38, 0.12], [0.55, 0.3], [0.72, 0.44], [0.9, 0.42], [1, 0.62]],
    links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6]]
  },
  {
    name: '仙后座',
    shape: [[0, 0.25], [0.22, 0.6], [0.4, 0.15], [0.6, 0.6], [0.82, 0.2]],
    links: [[0, 1], [1, 2], [2, 3], [3, 4]]
  },
  {
    name: '天鹅座',
    shape: [[0.5, 0], [0.5, 0.4], [0.5, 0.78], [0.5, 1], [0.16, 0.3], [0.84, 0.3]],
    links: [[0, 1], [1, 2], [2, 3], [1, 4], [1, 5]]
  },
  {
    name: '月华兔',
    shape: [[0.22, 0.05], [0.33, 0.16], [0.45, 0.05], [0.52, 0.2], [0.6, 0.34], [0.74, 0.42], [0.66, 0.58], [0.74, 0.8], [0.52, 0.9], [0.3, 0.8], [0.36, 0.58], [0.46, 0.44], [0.38, 0.3]],
    links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 10], [10, 11], [11, 12], [12, 3], [4, 12]]
  },
  {
    name: '星语蝶',
    shape: [[0.5, 0.42], [0.3, 0.12], [0.08, 0.28], [0.2, 0.5], [0.08, 0.72], [0.3, 0.86], [0.5, 0.6], [0.7, 0.86], [0.92, 0.72], [0.8, 0.5], [0.92, 0.28], [0.7, 0.12]],
    links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0], [0, 6], [6, 7], [7, 8], [8, 9], [9, 10], [10, 11], [11, 6]]
  },
  {
    name: '织梦鲸',
    shape: [[0.1, 0.44], [0.28, 0.26], [0.54, 0.2], [0.78, 0.3], [0.92, 0.44], [0.84, 0.58], [0.62, 0.64], [0.4, 0.6], [0.24, 0.7], [0.18, 0.55]],
    links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 0]]
  },
  {
    name: '流光琴',
    shape: [[0.24, 0.06], [0.76, 0.06], [0.86, 0.2], [0.76, 0.36], [0.24, 0.36], [0.14, 0.2], [0.34, 0.14], [0.5, 0.25], [0.66, 0.14]],
    links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0], [6, 7], [7, 8]]
  },
  {
    name: '幻月舟',
    shape: [[0.06, 0.52], [0.3, 0.26], [0.62, 0.2], [0.88, 0.44], [0.7, 0.62], [0.38, 0.68], [0.1, 0.62]],
    links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 0]]
  },
  {
    name: '青鸾',
    shape: [[0.5, 0.04], [0.42, 0.3], [0.2, 0.24], [0.3, 0.46], [0.06, 0.6], [0.34, 0.56], [0.5, 0.72], [0.68, 0.56], [0.94, 0.6], [0.7, 0.46], [0.8, 0.24], [0.58, 0.3]],
    links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 10], [10, 11], [11, 1]]
  },
  {
    name: '星辉冠',
    shape: [[0.08, 0.68], [0.08, 0.24], [0.28, 0.46], [0.4, 0.1], [0.5, 0.36], [0.6, 0.1], [0.72, 0.46], [0.92, 0.24], [0.92, 0.68]],
    links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 0]]
  },
  {
    name: '时光沙漏',
    shape: [[0.2, 0.06], [0.8, 0.06], [0.56, 0.5], [0.8, 0.94], [0.2, 0.94], [0.44, 0.5]],
    links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0]]
  }
]

export default {
  name: 'Universe',
  components: { HeaderNav },
  data() {
    return {
      stage: 'gate',
      doorOpen: false,
      arrayBreaking: false,
      flashOn: false,
      // 罗盘时钟整体尺寸（随视口自适应）
      clockSize: 700,
      // 级联表盘就绪标记（避免首次定位时出现过渡动画）
      clockReady: false,
      portalRunes: ['乾', '坤', '震', '巽', '坎', '离', '艮', '兑'],
      // 时间单位由外向内递减：秒 → 分 → 时 → 日 → 月 → 年(生肖)
      // 外圈转一圈，内圈跳一个刻度（机械级联）
      clockRings: [
        { ratio: 0.87, size: 12, step: false, sec: true, items: Array.from({ length: 60 }, (v, i) => String(i).padStart(2, '0')) },
        { ratio: 0.765, size: 12, step: true, items: Array.from({ length: 60 }, (v, i) => String(i).padStart(2, '0')) },
        { ratio: 0.66, size: 13, step: true, items: Array.from({ length: 24 }, (v, i) => i + '时') },
        { ratio: 0.555, size: 11, step: true, items: Array.from({ length: 31 }, (v, i) => (i + 1) + '号') },
        { ratio: 0.45, size: 13, step: true, items: ['正月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '冬月', '腊月'] },
        { ratio: 0.345, size: 14, step: true, items: ['鼠', '牛', '虎', '兔', '龙', '蛇', '马', '羊', '猴', '鸡', '狗', '猪'] }
      ],
      // 歌词轮播状态
      showLines: [],
      lyricIdx: 0,
      lyricOffset: 0,
      // 卡拉OK逐字点亮：主唱行已点亮的字数 / 是否正在滚动
      lyricStep: 0,
      lyricScrolling: false,
      // 最外圈：12 星座符号
      zodiacSymbols: ['♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓'],
      // 长按爆炸：脉冲波数组
      pulses: []
    }
  },
  mounted() {
    this.initUniverse()
    this.initClock()
    this.startLyrics()
  },
  beforeDestroy() {
    if (this._u && this._u.raf) cancelAnimationFrame(this._u.raf)
    if (this._lyricTimer) clearTimeout(this._lyricTimer)
    if (this._lyricAnim) this._lyricAnim.cancel()
    if (this._pressTimer) clearTimeout(this._pressTimer)
    this.clearLyricStepTimer()
    window.removeEventListener('resize', this.resize)
    window.removeEventListener('mousemove', this.onMouse)
    window.removeEventListener('touchmove', this.onTouch)
  },
  methods: {
    /* ================= 歌词轮播（从下往上滚动，无缝循环） ================= */
    // 初始填充：5 段可见 + 1 段衔接（藏在窗口下方）
    startLyrics() {
      this.showLines = LYRICS.slice(0, LYRIC_WINDOW_LINES + 1)
      this.lyricIdx = LYRIC_WINDOW_LINES + 1
      this._lyricRolling = false
      // 进入后立即开始第一行的逐字点亮（滚动由点亮完成事件驱动）
      this.startLyricStep()
    },

    // 用 Web Animations API 驱动滚动（避免 CSS transition「新启用」时不触发动画的问题）
    rollLyrics() {
      if (this._lyricRolling) return
      const track = this.$refs.track
      if (!track || typeof track.animate !== 'function') {
        // 场景尚未渲染完成，稍后重试
        this._lyricTimer = setTimeout(() => this.rollLyrics(), 500)
        return
      }
      this._lyricRolling = true
      // 滚动开始：熄灭已唱行、触发滚动光带，逐字点亮暂停
      this.lyricStep = 0
      this.lyricScrolling = true
      this.clearLyricStepTimer()
      if (this._lyricTimer) {
        clearTimeout(this._lyricTimer)
        this._lyricTimer = null
      }
      const anim = track.animate(
        [
          { transform: 'translateY(0px)' },
          { transform: 'translateY(' + (-LYRIC_LINE_H) + 'px)' }
        ],
        { duration: LYRIC_MOVE_MS, easing: 'linear', fill: 'forwards' }
      )
      this._lyricAnim = anim
      const done = () => {
        // 剔除首行、末尾补一行，并瞬间复位（动画末帧与复位画面一致 → 无缝）
        this.showLines.push(LYRICS[this.lyricIdx % LYRICS.length])
        this.showLines.shift()
        this.lyricIdx++
        this.lyricOffset = 0
        anim.cancel()
        this._lyricRolling = false
        this._lyricAnim = null
        this.lyricScrolling = false
        // 停顿期间：新一轮主唱行逐字点亮
        this.startLyricStep()
      }
      anim.onfinish = done
      // 兜底：动画异常未回调时也能继续轮播
      setTimeout(() => {
        if (this._lyricRolling && this._lyricAnim === anim) done()
      }, LYRIC_MOVE_MS + 500)
    },

    // 卡拉OK逐字点亮：所有行统一速度（LYRIC_CHAR_MS），整行点亮完成后 → 停留 → 滚动下一行
    startLyricStep() {
      this.clearLyricStepTimer()
      const active = this.showLines[2]
      if (!active) return
      const len = active.length
      this.lyricStep = 0
      this._lyricStepTimer = setInterval(() => {
        if (this.lyricStep < len) {
          this.lyricStep++
        } else {
          this.clearLyricStepTimer()
          // 整行已点亮：停留片刻后滚动到下一行
          this._lyricTimer = setTimeout(() => this.rollLyrics(), LYRIC_PAUSE_MS)
        }
      }, LYRIC_CHAR_MS)
    },

    clearLyricStepTimer() {
      if (this._lyricStepTimer) {
        clearInterval(this._lyricStepTimer)
        this._lyricStepTimer = null
      }
    },

    /* ================= 初始化 ================= */
    initUniverse() {
      const cv = this.$refs.canvas
      const ctx = cv.getContext('2d')
      this._u = {
        cv,
        ctx,
        w: 0,
        h: 0,
        dpr: 1,
        t: 0,
        mx: -9999,
        my: -9999,
        mouseOn: false,
        riverI: 0,
        stars: [],
        floaters: [],
        constellations: [],
        river: [],
        swirl: [],
        bursts: [],
        pulses: [],
        blastFlash: 0,
        nebula: [],
        sprites: {},
        portal: { x: 0, y: 0 }
      }
      // 级联表盘各内圈的上一刻度值（用于判断是否需要跳动）
      this._clockPrev = [null, null, null, null, null]
      this._clockRingsSeen = false
      // 刻度角度累加偏移（跨圈进位时避免反向回转动画）
      this._secOff = 0
      this._ringOff = [0, 0, 0, 0, 0]
      this._lastSec = null
      this.buildSprites()
      this.resize()
      window.addEventListener('resize', this.resize)
      window.addEventListener('mousemove', this.onMouse)
      window.addEventListener('touchmove', this.onTouch, { passive: true })
      this._u.raf = requestAnimationFrame(this.tick)
    },

    resize() {
      const u = this._u
      if (!u) return
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const w = this.$refs.scene.clientWidth
      const h = this.$refs.scene.clientHeight
      u.w = w
      u.h = h
      u.dpr = dpr
      u.cv.width = Math.round(w * dpr)
      u.cv.height = Math.round(h * dpr)
      u.cv.style.width = w + 'px'
      u.cv.style.height = h + 'px'
      u.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      // 罗盘时钟整体随视口自适应；神秘之门位于时钟正中央
      const clockSize = Math.max(400, Math.min(620, Math.round(w * 0.4)))
      this.clockSize = clockSize
      u.portal = { x: w - w * 0.035 - clockSize / 2, y: h * 0.52 }
      this.initStars()
      this.initNebula()
      this.initFloaters()
      this.initConstellations()
      this.initRiver()
      this.initSwirl()
      this.updateClockRings(true)
    },

    // 预渲染发光精灵（避免每帧 shadowBlur，性能更优）
    buildSprites() {
      const u = this._u
      const mk = (color, size) => {
        const c = document.createElement('canvas')
        c.width = size
        c.height = size
        const g = c.getContext('2d')
        const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
        grad.addColorStop(0, '#ffffff')
        grad.addColorStop(0.25, color)
        grad.addColorStop(1, 'rgba(0,0,0,0)')
        g.fillStyle = grad
        g.fillRect(0, 0, size, size)
        return c
      }
      u.sprites = {
        white: mk('rgba(255,255,255,0.95)', 64),
        pink: mk('rgba(255,111,216,0.9)', 64),
        cyan: mk('rgba(55,216,255,0.9)', 64),
        gold: mk('rgba(255,215,107,0.9)', 64),
        purple: mk('rgba(155,107,255,0.9)', 64)
      }
    },

    /* ================= 系统初始化 ================= */
    initStars() {
      const u = this._u
      const count = Math.max(280, Math.min(820, Math.round((u.w * u.h) / 2400)))
      u.stars = []
      for (let i = 0; i < count; i++) {
        u.stars.push({
          x: Math.random() * u.w,
          y: Math.random() * u.h,
          z: 0.3 + Math.random() * 0.7,
          r: 0.4 + Math.random() * 1.5,
          tw: 0.5 + Math.random() * 2.2,
          ph: Math.random() * TAU,
          c: STAR_COLORS[(Math.random() * STAR_COLORS.length) | 0],
          glow: Math.random() < 0.08
        })
      }
    },

    initNebula() {
      const u = this._u
      const colors = [
        { c: 'rgba(255,80,190,0.5)', s: 'pink' },
        { c: 'rgba(120,60,255,0.5)', s: 'purple' },
        { c: 'rgba(40,180,255,0.45)', s: 'cyan' }
      ]
      u.nebula = []
      for (let i = 0; i < 4; i++) {
        const col = colors[i % colors.length]
        u.nebula.push({
          x: Math.random() * u.w,
          y: Math.random() * u.h,
          vx: (Math.random() - 0.5) * 0.12,
          vy: (Math.random() - 0.5) * 0.1,
          size: 320 + Math.random() * 280,
          sprite: col.s,
          alpha: 0.1 + Math.random() * 0.1
        })
      }
    },

    initFloaters() {
      const u = this._u
      const count = Math.max(70, Math.min(200, Math.round((u.w * u.h) / 16000)))
      u.floaters = []
      for (let i = 0; i < count; i++) {
        u.floaters.push({
          x: Math.random() * u.w,
          y: Math.random() * u.h,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.4,
          r: 1.2 + Math.random() * 2,
          sprite: PARTICLE_SPRITES[(Math.random() * PARTICLE_SPRITES.length) | 0],
          tw: 0.8 + Math.random() * 2,
          ph: Math.random() * TAU
        })
      }
    },

    initConstellations() {
      const u = this._u
      u.constellations = CONSTELLATIONS.map(def => {
        const stars = def.shape.map(s => ({
          sx: s[0],
          sy: s[1],
          ox: 0,
          oy: 0,
          r: 1.6 + Math.random() * 1.8,
          tw: 0.5 + Math.random() * 1.5,
          ph: Math.random() * TAU
        }))
        return {
          name: def.name,
          shape: def.shape,
          links: def.links,
          stars,
          cx: Math.random() * u.w,
          cy: u.h * 0.15 + Math.random() * u.h * 0.7,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.24,
          scale: 70 + Math.random() * 60
        }
      })
    },

    initRiver() {
      const u = this._u
      u.river = []
      for (let i = 0; i < 260; i++) {
        u.river.push({
          p: Math.random(),
          speed: 0.0036 + Math.random() * 0.0048,
          seed: Math.random() * TAU,
          size: 1.2 + Math.random() * 1.6,
          bright: Math.random(),
          // 横向偏移系数（-1~1），随河道宽度缩放：源头开阔、入口收窄
          lat: Math.random() * 2 - 1
        })
      }
    },

    initSwirl() {
      const u = this._u
      u.swirl = []
      for (let i = 0; i < 46; i++) {
        u.swirl.push({
          ang: Math.random() * TAU,
          speed: 0.015 + Math.random() * 0.025,
          rad: 30 + Math.random() * 120,
          seed: Math.random() * TAU,
          sprite: Math.random() < 0.5 ? 'pink' : 'cyan'
        })
      }
    },

    /* ================= 事件 ================= */
    onMouse(e) {
      const u = this._u
      u.mx = e.clientX
      u.my = e.clientY
      u.mouseOn = true
      if (this.$refs.cursor) {
        this.$refs.cursor.style.left = e.clientX + 'px'
        this.$refs.cursor.style.top = e.clientY + 'px'
      }
    },

    onTouch(e) {
      const u = this._u
      if (e.touches && e.touches[0]) {
        u.mx = e.touches[0].clientX
        u.my = e.touches[0].clientY
        u.mouseOn = true
      }
    },

    // 鼠标排斥力
    repulse(px, py, R, strength) {
      const u = this._u
      if (!u.mouseOn) return { x: 0, y: 0 }
      const dx = px - u.mx
      const dy = py - u.my
      const d2 = dx * dx + dy * dy
      if (d2 > R * R || d2 === 0) return { x: 0, y: 0 }
      const d = Math.sqrt(d2)
      const f = (1 - d / R) * strength
      return { x: (dx / d) * f, y: (dy / d) * f }
    },

    /* ================= 罗盘时钟（机械级联） ================= */
    // 时钟整体尺寸
    clockStyle() {
      return {
        width: this.clockSize + 'px',
        height: this.clockSize + 'px'
      }
    },

    // 菱形花纹外框尺寸（略大于钟面，四个角向外探出）
    frameStyle() {
      const s = Math.round(this.clockSize * 1.26)
      return {
        width: s + 'px',
        height: s + 'px'
      }
    },

    // 中央神秘之门尺寸
    portalInnerStyle() {
      return {
        width: (this.clockSize * 0.26).toFixed(1) + 'px',
        height: (this.clockSize * 0.42).toFixed(1) + 'px'
      }
    },

    // 文字按所在圈排布成圆环（放射状：文字沿半径向外，读取框位置为正立可读）
    ringItemStyle(ring, ti) {
      const n = ring.items.length
      const a = (ti / n) * 360 + 90
      const H = this.clockSize / 2
      const r = ring.ratio * H
      const fs = ring.size * (this.clockSize / 700)
      return {
        left: '50%',
        top: '50%',
        fontSize: fs.toFixed(1) + 'px',
        transform: 'translate(-50%, -50%) rotate(' + a.toFixed(2) + 'deg) translateY(' + (-r).toFixed(1) + 'px)'
      }
    },

    // 最外圈星座符号位置
    zodiacStyle(i) {
      const n = 12
      const a = (i / n) * 360 + 90
      const H = this.clockSize / 2
      const r = 0.955 * H
      const fs = 11 * (this.clockSize / 700)
      return {
        left: '50%',
        top: '50%',
        fontSize: fs.toFixed(1) + 'px',
        transform: 'translate(-50%, -50%) rotate(' + a.toFixed(2) + 'deg) translateY(' + (-r).toFixed(1) + 'px)'
      }
    },

    // 初始化表盘：先定位到当前时刻，再开启级联跳动过渡
    initClock() {
      this.updateClockRings(true)
      this.$nextTick(() => {
        this.clockReady = true
      })
    },

    // 级联驱动：最外圈秒针圈每秒跳一个刻度，内圈到点进位（机械式）
    updateClockRings(force) {
      const rings = this.$refs.clockRing
      if (!rings || !rings.length) {
        this._clockRingsSeen = false
        return
      }
      const first = !this._clockRingsSeen
      this._clockRingsSeen = true
      const now = new Date()
      const sec = now.getSeconds()
      // 秒圈：每秒一格；59→0 进位时角度累加，保证跨圈后继续同向旋转、不倒回 00
      if (first || this._lastSec === null) {
        this._lastSec = sec
      } else if (sec < this._lastSec) {
        this._secOff += 360
      }
      this._lastSec = sec
      rings[0].style.transform = 'rotate(' + (-(sec / 60) * 360 - this._secOff).toFixed(3) + 'deg)'
      // 内圈：分 / 时 / 日 / 月 / 年（生肖），同样以累加角度进位
      const vals = [now.getMinutes(), now.getHours(), now.getDate() - 1, now.getMonth(), now.getFullYear() % 12]
      const totals = [60, 24, 31, 12, 12]
      if (first) {
        for (let i = 0; i < rings.length; i++) rings[i].classList.remove('step', 'sec')
      }
      for (let i = 1; i < rings.length; i++) {
        const v = vals[i - 1]
        let off = this._ringOff[i - 1]
        if (this._clockPrev[i - 1] !== null && v < this._clockPrev[i - 1]) {
          off += 360
          this._ringOff[i - 1] = off
        }
        if (first || force || v !== this._clockPrev[i - 1]) {
          rings[i].style.transform = 'rotate(' + (-(v / totals[i - 1]) * 360 - off).toFixed(3) + 'deg)'
          this._clockPrev[i - 1] = v
        }
      }
      if (first) {
        requestAnimationFrame(() => {
          for (let i = 1; i < rings.length; i++) rings[i].classList.add('step')
          rings[0].classList.add('sec')
        })
      }
    },

    runeStyle(i, radius) {
      const n = 8
      const a = (i / n) * TAU - Math.PI / 2
      const x = Math.cos(a) * radius
      const y = Math.sin(a) * radius
      return {
        left: 'calc(50% + ' + x.toFixed(2) + 'px)',
        top: 'calc(50% + ' + y.toFixed(2) + 'px)',
        transform: 'translate(-50%, -50%)'
      }
    },

    /* ================= 开启时空之门 ================= */
    openGate() {
      if (this.doorOpen || this.arrayBreaking) return
      this.arrayBreaking = true
      // 法阵爆裂粒子
      const rect = this.$refs.seal.getBoundingClientRect()
      this.spawnBurst(rect.left + rect.width / 2, rect.top + rect.height / 2, 90)
      setTimeout(() => {
        this.doorOpen = true
        this.flashOn = true
        setTimeout(() => {
          this.flashOn = false
        }, 420)
      }, 620)
      setTimeout(() => {
        this.stage = 'cosmos'
      }, 2000)
    },

    spawnBurst(cx, cy, count) {
      const u = this._u
      for (let i = 0; i < count; i++) {
        const a = Math.random() * TAU
        const sp = 2.5 + Math.random() * 7
        const sprites = ['pink', 'cyan', 'gold', 'purple']
        u.bursts.push({
          x: cx,
          y: cy,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp,
          life: 1,
          decay: 0.008 + Math.random() * 0.014,
          size: 2.5 + Math.random() * 3.5,
          sprite: sprites[(Math.random() * sprites.length) | 0]
        })
      }
    },

    /* ============ 点击中心：星球爆炸 + 脉冲波 ============ */
    onPortalClick() {
      this.spawnExplosion()
    },

    // 星球爆炸：大量粒子 + 多层脉冲波
    spawnExplosion() {
      const u = this._u
      const cx = u.portal.x
      const cy = u.portal.y
      // 爆炸粒子：更快更多、范围更大
      for (let i = 0; i < 180; i++) {
        const a = Math.random() * TAU
        const sp = 3 + Math.random() * 12
        const sprites = ['pink', 'cyan', 'gold', 'purple', 'white']
        u.bursts.push({
          x: cx, y: cy,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp,
          life: 1,
          decay: 0.006 + Math.random() * 0.012,
          size: 2 + Math.random() * 5,
          sprite: sprites[(Math.random() * sprites.length) | 0]
        })
      }
      // 3 层脉冲波，错开时间
      for (let k = 0; k < 3; k++) {
        u.pulses.push({
          r: 10,
          maxR: 180 + k * 70,
          speed: 2.5 + k * 0.8,
          life: 1,
          decay: 0.012,
          delay: k * 8
        })
      }
      // 中心闪光（canvas 上画，不是全屏）
      u.blastFlash = 1
    },

    drawPulses(ctx, u) {
      if (u.pulses.length === 0) return
      ctx.globalCompositeOperation = 'lighter'
      for (let i = u.pulses.length - 1; i >= 0; i--) {
        const p = u.pulses[i]
        if (p.delay > 0) { p.delay--; continue }
        p.r += p.speed
        p.life -= p.decay
        if (p.life <= 0 || p.r > p.maxR) {
          u.pulses.splice(i, 1)
          continue
        }
        const alpha = p.life * 0.6
        ctx.strokeStyle = 'rgba(255, 200, 240,' + alpha.toFixed(3) + ')'
        ctx.lineWidth = 2.5 * p.life + 0.5
        ctx.beginPath()
        ctx.arc(u.portal.x, u.portal.y, p.r, 0, TAU)
        ctx.stroke()
        // 内层辉光
        ctx.strokeStyle = 'rgba(180, 140, 255,' + (alpha * 0.5).toFixed(3) + ')'
        ctx.lineWidth = 6 * p.life
        ctx.beginPath()
        ctx.arc(u.portal.x, u.portal.y, p.r * 0.92, 0, TAU)
        ctx.stroke()
      }
      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
    },

    /* ================= 绘制 ================= */
    tick() {
      const u = this._u
      if (!u) return
      u.t += 1
      const ctx = u.ctx
      const t = u.t

      // 罗盘时钟级联驱动（秒圈连续、内圈跳刻度）
      this.updateClockRings(false)

      // 时间长河强度：开门后逐渐增强
      const target = this.stage === 'cosmos' ? 1 : 0.1
      u.riverI += (target - u.riverI) * 0.018

      ctx.clearRect(0, 0, u.w, u.h)

      this.drawNebula(ctx, u, t)
      this.drawStars(ctx, u, t)
      if (u.riverI > 0.01) {
        this.drawRiver(ctx, u)
        this.drawSwirl(ctx, u)
      }
      this.drawConstellations(ctx, u, t)
      this.drawFloaters(ctx, u, t)
      this.drawBursts(ctx, u)
      this.drawPulses(ctx, u)
      if (u.riverI > 0.01) this.drawPortalGlow(ctx, u, t)
      this.drawBlastFlash(ctx, u)

      u.raf = requestAnimationFrame(this.tick)
    },

    drawNebula(ctx, u, t) {
      ctx.globalCompositeOperation = 'lighter'
      for (const n of u.nebula) {
        n.x += n.vx
        n.y += n.vy
        if (n.x < -n.size) n.x = u.w + n.size
        if (n.x > u.w + n.size) n.x = -n.size
        if (n.y < -n.size) n.y = u.h + n.size
        if (n.y > u.h + n.size) n.y = -n.size
        const pulse = 0.75 + 0.25 * Math.sin(t * 0.008 + n.x * 0.01)
        ctx.globalAlpha = n.alpha * pulse
        ctx.drawImage(u.sprites[n.sprite], n.x - n.size / 2, n.y - n.size / 2, n.size, n.size)
      }
      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
    },

    drawStars(ctx, u, t) {
      const pxOff = u.mouseOn ? (u.mx - u.w / 2) * 0.014 : 0
      const pyOff = u.mouseOn ? (u.my - u.h / 2) * 0.014 : 0
      for (const s of u.stars) {
        const tw = 0.5 + 0.5 * Math.sin(t * 0.02 * s.tw + s.ph)
        let x = (s.x + pxOff * s.z) % u.w
        let y = (s.y + pyOff * s.z) % u.h
        if (x < 0) x += u.w
        if (y < 0) y += u.h
        ctx.globalAlpha = 0.25 + 0.7 * tw
        ctx.fillStyle = s.c
        ctx.beginPath()
        ctx.arc(x, y, s.r, 0, TAU)
        ctx.fill()
        if (s.glow) {
          ctx.globalAlpha = (0.3 + 0.5 * tw) * 0.6
          ctx.drawImage(u.sprites.white, x - 7, y - 7, 14, 14)
        }
      }
      ctx.globalAlpha = 1
    },

    drawFloaters(ctx, u, t) {
      for (const p of u.floaters) {
        p.x += p.vx
        p.y += p.vy
        if (p.x < -10) p.x = u.w + 10
        if (p.x > u.w + 10) p.x = -10
        if (p.y < -10) p.y = u.h + 10
        if (p.y > u.h + 10) p.y = -10
        const rep = this.repulse(p.x, p.y, 150, 26)
        p.vx += rep.x * 0.002
        p.vy += rep.y * 0.002
        const dx = p.x + rep.x
        const dy = p.y + rep.y
        const tw = 0.55 + 0.45 * Math.sin(t * 0.03 * p.tw + p.ph)
        const sprite = u.sprites[p.sprite]
        const gs = p.r * 9
        ctx.globalAlpha = 0.5 * tw
        ctx.drawImage(sprite, dx - gs / 2, dy - gs / 2, gs, gs)
        ctx.globalAlpha = 0.9 * tw
        ctx.fillStyle = '#ffffff'
        ctx.beginPath()
        ctx.arc(dx, dy, p.r * 0.7, 0, TAU)
        ctx.fill()
      }
      ctx.globalAlpha = 1
    },

    drawConstellations(ctx, u, t) {
      ctx.globalCompositeOperation = 'lighter'
      for (const g of u.constellations) {
        g.cx += g.vx
        g.cy += g.vy
        if (g.cx < -200) g.cx = u.w + 200
        if (g.cx > u.w + 200) g.cx = -200
        if (g.cy < -200) g.cy = u.h + 200
        if (g.cy > u.h + 200) g.cy = -200

        const pts = g.stars.map(s => {
          const bx = g.cx + s.sx * g.scale
          const by = g.cy + s.sy * g.scale
          const rep = this.repulse(bx, by, 130, 30)
          s.ox += (rep.x - s.ox) * 0.12
          s.oy += (rep.y - s.oy) * 0.12
          return { x: bx + s.ox, y: by + s.oy, r: s.r, tw: s.tw, ph: s.ph }
        })

        // 连线
        ctx.strokeStyle = 'rgba(170,215,255,0.28)'
        ctx.lineWidth = 1
        ctx.beginPath()
        for (const [a, b] of g.links) {
          ctx.moveTo(pts[a].x, pts[a].y)
          ctx.lineTo(pts[b].x, pts[b].y)
        }
        ctx.stroke()

        // 星点
        for (let i = 0; i < pts.length; i++) {
          const p = pts[i]
          const tw = 0.5 + 0.5 * Math.sin(t * 0.025 * p.tw + p.ph)
          const gs = p.r * 8
          ctx.globalAlpha = 0.55 * tw
          ctx.drawImage(u.sprites.cyan, p.x - gs / 2, p.y - gs / 2, gs, gs)
          ctx.globalAlpha = 0.95
          ctx.fillStyle = '#ffffff'
          ctx.beginPath()
          ctx.arc(p.x, p.y, p.r * 0.65, 0, TAU)
          ctx.fill()
        }

        // 星座名
        ctx.globalAlpha = 0.45
        ctx.fillStyle = '#d7ecff'
        ctx.font = '11px "Microsoft YaHei", sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText(g.name, g.cx, g.cy + g.scale * 0.62 + 20)
      }
      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
    },

    // 时间长河路径：源头在屏幕左下角外（不可见），S 形流入罗盘时钟中央的神秘之门
    riverPoint(u, p) {
      const p0 = { x: -20, y: u.h * 1.00 }
      const c1 = { x: u.w * 0.24, y: u.h * 0.1 }
      const c2 = { x: u.w * 0.6, y: u.h * 0.9 }
      const p1 = u.portal
      const mt = 1 - p
      const a = mt * mt * mt
      const b = 3 * mt * mt * p
      const c = 3 * mt * p * p
      const d = p * p * p
      return {
        x: a * p0.x + b * c1.x + c * c2.x + d * p1.x,
        y: a * p0.y + b * c1.y + c * c2.y + d * p1.y
      }
    },

    // 河道宽度：源头宽、入门口窄（透视感：流向远方）
    riverWidth(p) {
      return 92 * Math.pow(1 - p, 1.35) + 9
    },

    // 河道法线（用于粒子横向铺开 / 收窄）
    riverNormal(u, p) {
      const e = 0.012
      const A = this.riverPoint(u, Math.max(0, p - e))
      const B = this.riverPoint(u, Math.min(1, p + e))
      let nx = -(B.y - A.y)
      let ny = B.x - A.x
      const len = Math.sqrt(nx * nx + ny * ny) || 1
      return { x: nx / len, y: ny / len }
    },

    riverColor(p) {
      const stops = [
        [0, [69, 224, 255]],
        [0.3, [127, 157, 255]],
        [0.55, [180, 123, 255]],
        [0.75, [255, 122, 209]],
        [1, [255, 158, 230]]
      ]
      let a = stops[0]
      let b = stops[stops.length - 1]
      for (let i = 0; i < stops.length - 1; i++) {
        if (p >= stops[i][0] && p <= stops[i + 1][0]) {
          a = stops[i]
          b = stops[i + 1]
          break
        }
      }
      const span = Math.max(0.0001, b[0] - a[0])
      const k = Math.min(1, Math.max(0, (p - a[0]) / span))
      const c1 = a[1]
      const c2 = b[1]
      const r = Math.round(c1[0] + (c2[0] - c1[0]) * k)
      const g = Math.round(c1[1] + (c2[1] - c1[1]) * k)
      const bl = Math.round(c1[2] + (c2[2] - c1[2]) * k)
      return 'rgb(' + r + ',' + g + ',' + bl + ')'
    },

    drawRiver(ctx, u) {
      const intensity = u.riverI
      ctx.globalCompositeOperation = 'lighter'

      // 细亮芯：仅一条淡光路径（不再是胶囊形宽光带），主体由时间粒子构成
      const segs = 36
      for (let i = 0; i < segs; i++) {
        const p = i / segs
        const pt = this.riverPoint(u, p)
        const n = this.riverNormal(u, p)
        const w = Math.max(1.2, this.riverWidth(p) * 0.16)
        ctx.strokeStyle = this.riverColor(p)
        ctx.globalAlpha = (0.07 + 0.07 * p) * intensity
        ctx.lineWidth = w
        ctx.lineCap = 'round'
        ctx.beginPath()
        ctx.moveTo(pt.x - n.x * w / 2, pt.y - n.y * w / 2)
        ctx.lineTo(pt.x + n.x * w / 2, pt.y + n.y * w / 2)
        ctx.stroke()
      }

      // 时间光束粒子：源头铺得开、入门口收窄聚拢（越近门越拥挤）
      for (const r of u.river) {
        // 靠近入口流速变慢，粒子堆积更密集
        r.p += r.speed * (1 - 0.5 * r.p)
        if (r.p > 1.03) {
          // 回源：在屏幕外重新生成，屏幕内看不到源头粒子冒出
          r.p = -0.08
          r.seed = Math.random() * TAU
          r.lat = Math.random() * 2 - 1
        }
        const p = Math.max(0, r.p)
        const raw = this.riverPoint(u, p)
        const n = this.riverNormal(u, p)
        const halfW = this.riverWidth(p) / 2
        const xx = raw.x + n.x * r.lat * halfW
        const yy = raw.y + n.y * r.lat * halfW
        // 轻排斥：鼠标轻推开粒子，不破坏河流整体
        const rep = this.repulse(xx, yy, 100, 50)
        const fx = xx + rep.x
        const fy = yy + rep.y

        // 末尾渐隐入神秘之门
        let fade = 1
        if (p > 0.9) fade = Math.max(0, 1 - (p - 0.9) / 0.13)

        // 多段渐隐拖尾：头部亮粗、尾部细淡，长度随粒子亮度浮动
        const tailSegs = 5
        const tailLen = 0.02 + r.bright * 0.055
        const headAlpha = (0.25 + r.bright * 0.55) * intensity * fade
        const headWidth = 0.7 + r.bright * 1.3
        for (let s = 0; s < tailSegs; s++) {
          const t0 = s / tailSegs
          const t1 = (s + 1) / tailSegs
          const pA = Math.max(0, p - tailLen * (1 - t1))
          const pB = Math.max(0, p - tailLen * (1 - t0))
          const rawA = this.riverPoint(u, pA)
          const nA = this.riverNormal(u, pA)
          const halfWA = this.riverWidth(pA) / 2
          let xA = rawA.x + nA.x * r.lat * halfWA
          let yA = rawA.y + nA.y * r.lat * halfWA
          const rawB = this.riverPoint(u, pB)
          const nB = this.riverNormal(u, pB)
          const halfWB = this.riverWidth(pB) / 2
          let xB = rawB.x + nB.x * r.lat * halfWB
          let yB = rawB.y + nB.y * r.lat * halfWB
          // 每段独立应用鼠标排斥，拖尾跟随头部运动
          const repA = this.repulse(xA, yA, 100, 50)
          xA += repA.x
          yA += repA.y
          const repB = this.repulse(xB, yB, 100, 50)
          xB += repB.x
          yB += repB.y
          const segK = (s + 1) / tailSegs
          ctx.strokeStyle = this.riverColor((pA + pB) / 2)
          ctx.globalAlpha = headAlpha * segK
          ctx.lineWidth = Math.max(0.25, headWidth * segK)
          ctx.beginPath()
          ctx.moveTo(xA, yA)
          ctx.lineTo(xB, yB)
          ctx.stroke()
        }

        // 粒子头部辉光
        const gs = (5 + r.size * 5) * (0.6 + r.bright * 0.8)
        ctx.globalAlpha = headAlpha * 0.7
        ctx.drawImage(u.sprites.white, fx - gs / 2, fy - gs / 2, gs, gs)
      }

      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
    },

    drawSwirl(ctx, u) {
      const intensity = u.riverI
      ctx.globalCompositeOperation = 'lighter'
      for (const s of u.swirl) {
        s.ang += s.speed
        s.rad *= 0.985
        if (s.rad < 5) {
          s.rad = 40 + Math.random() * 110
          s.ang = Math.random() * TAU
        }
        const x = u.portal.x + Math.cos(s.ang) * s.rad
        const y = u.portal.y + Math.sin(s.ang) * s.rad * 0.82
        const a = Math.min(1, s.rad / 110) * 0.8 * intensity
        const sprite = u.sprites[s.sprite]
        const gs = 8 + s.rad * 0.12
        ctx.globalAlpha = a
        ctx.drawImage(sprite, x - gs / 2, y - gs / 2, gs, gs)
      }
      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
    },

    drawPortalGlow(ctx, u, t) {
      const intensity = u.riverI
      const pulse = 0.7 + 0.3 * Math.sin(t * 0.03)
      ctx.globalCompositeOperation = 'lighter'
      const grad = ctx.createRadialGradient(u.portal.x, u.portal.y, 0, u.portal.x, u.portal.y, 110)
      grad.addColorStop(0, 'rgba(255,255,255,0.55)')
      grad.addColorStop(0.35, 'rgba(255,111,216,0.28)')
      grad.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.globalAlpha = 0.9 * intensity * pulse
      ctx.fillStyle = grad
      ctx.beginPath()
      ctx.arc(u.portal.x, u.portal.y, 110, 0, TAU)
      ctx.fill()
      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
    },

    // 爆炸中心闪光：在罗盘中心位置扩散
    drawBlastFlash(ctx, u) {
      if (u.blastFlash <= 0) return
      const k = u.blastFlash
      ctx.globalCompositeOperation = 'lighter'
      const r = 260 * (1.3 - k * 0.3)
      const grad = ctx.createRadialGradient(u.portal.x, u.portal.y, 0, u.portal.x, u.portal.y, r)
      grad.addColorStop(0, 'rgba(255,255,255,' + (1.0 * k).toFixed(3) + ')')
      grad.addColorStop(0.25, 'rgba(255,200,240,' + (0.7 * k).toFixed(3) + ')')
      grad.addColorStop(0.6, 'rgba(180,120,255,' + (0.35 * k).toFixed(3) + ')')
      grad.addColorStop(1, 'rgba(123,63,242,0)')
      ctx.fillStyle = grad
      ctx.beginPath()
      ctx.arc(u.portal.x, u.portal.y, r, 0, TAU)
      ctx.fill()
      u.blastFlash -= 0.035
      if (u.blastFlash < 0) u.blastFlash = 0
      ctx.globalCompositeOperation = 'source-over'
    },

    drawBursts(ctx, u) {
      if (u.bursts.length === 0) return
      ctx.globalCompositeOperation = 'lighter'
      for (let i = u.bursts.length - 1; i >= 0; i--) {
        const b = u.bursts[i]
        b.x += b.vx
        b.y += b.vy
        b.vx *= 0.985
        b.vy *= 0.985
        b.life -= b.decay
        if (b.life <= 0) {
          u.bursts.splice(i, 1)
          continue
        }
        const sprite = u.sprites[b.sprite]
        const gs = b.size * 9 * b.life
        ctx.globalAlpha = b.life * 0.9
        ctx.drawImage(sprite, b.x - gs / 2, b.y - gs / 2, gs, gs)
        ctx.globalAlpha = b.life
        ctx.fillStyle = '#ffffff'
        ctx.beginPath()
        ctx.arc(b.x, b.y, b.size * 0.8 * b.life, 0, TAU)
        ctx.fill()
      }
      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
    }
  }
}
</script>

<style scoped>
.universe-page {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* ============ 场景容器 ============ */
.universe-scene {
  position: fixed;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  background:
    radial-gradient(1200px 800px at 18% 18%, #1b0b3a 0%, transparent 60%),
    radial-gradient(1000px 700px at 82% 28%, #0a1c3a 0%, transparent 55%),
    radial-gradient(900px 900px at 62% 92%, #2a0a3a 0%, transparent 60%),
    linear-gradient(180deg, #04010e 0%, #0a0320 45%, #05020f 100%);
}

.u-canvas {
  position: absolute;
  inset: 0;
  display: block;
}

/* 鼠标光晕 */
.u-cursor {
  position: absolute;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  margin: -45px 0 0 -45px;
  pointer-events: none;
  z-index: 40;
  background: radial-gradient(circle, rgba(255, 170, 230, 0.22) 0%, rgba(120, 120, 255, 0.1) 40%, transparent 70%);
  mix-blend-mode: screen;
}

/* 开门闪光 */
.flash {
  position: absolute;
  inset: 0;
  z-index: 900;
  pointer-events: none;
  opacity: 0;
  background: radial-gradient(circle at 50% 50%,
      rgba(255, 255, 255, 0.95),
      rgba(255, 150, 220, 0.55) 30%,
      rgba(120, 80, 255, 0.25) 55%,
      transparent 78%);
}
.flash.on {
  animation: flashOnce 0.5s ease-out forwards;
}
@keyframes flashOnce {
  0% { opacity: 0; }
  18% { opacity: 1; }
  100% { opacity: 0; }
}

/* ============ 阶段一：时空之门 ============ */
.gate-stage {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gate-out-leave-active {
  transition: opacity 0.9s ease 0.15s;
}
.gate-out-leave-to {
  opacity: 0;
}

.gate-wrap {
  --W: min(500px, 60vmin);
  position: relative;
  width: var(--W);
  height: calc(var(--W) * 1.16);
}

/* 门框 */
.gate-frame {
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  border: 3px solid rgba(255, 170, 225, 0.7);
  border-radius: calc(var(--W) / 2) calc(var(--W) / 2) 26px 26px;
  box-shadow:
    0 0 42px rgba(255, 120, 200, 0.4),
    0 0 90px rgba(123, 63, 242, 0.28),
    inset 0 0 46px rgba(120, 80, 255, 0.22),
    inset 0 0 8px rgba(255, 200, 235, 0.35);
}

.gate-frame::before {
  content: '';
  position: absolute;
  inset: 9px;
  border-radius: calc(var(--W) / 2 - 9px) calc(var(--W) / 2 - 9px) 18px 18px;
  border: 1px dashed rgba(55, 216, 255, 0.5);
  box-shadow: inset 0 0 26px rgba(55, 216, 255, 0.12);
}

/* 拱顶宝石 */
.frame-gem {
  position: absolute;
  top: -14px;
  left: 50%;
  width: 26px;
  height: 26px;
  transform: translateX(-50%) rotate(45deg);
  background: linear-gradient(135deg, #fff, #ff8ad4 55%, #7b3ff2);
  border-radius: 6px;
  box-shadow: 0 0 22px rgba(255, 138, 212, 0.95), 0 0 44px rgba(123, 63, 242, 0.6);
  animation: gemPulse 2.6s ease-in-out infinite;
}
@keyframes gemPulse {
  0%, 100% { box-shadow: 0 0 18px rgba(255, 138, 212, 0.85), 0 0 40px rgba(123, 63, 242, 0.5); }
  50% { box-shadow: 0 0 30px rgba(255, 190, 230, 1), 0 0 70px rgba(123, 63, 242, 0.85); }
}

/* 门扇 */
.door-panel {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 50.4%;
  z-index: 2;
  overflow: hidden;
  background:
    radial-gradient(120% 60% at 50% 0%, rgba(255, 120, 200, 0.14), transparent 55%),
    linear-gradient(165deg, #231040 0%, #120626 32%, #1d0b38 62%, #0b0318 100%);
  box-shadow: inset 0 0 30px rgba(0, 0, 0, 0.65);
  transition: transform 1.35s cubic-bezier(0.65, 0, 0.25, 1) 0.12s;
  will-change: transform;
}
.panel-left {
  left: 0;
  border-top-left-radius: calc(var(--W) / 2);
  border-right: 1px solid rgba(255, 160, 220, 0.35);
}
.panel-right {
  right: 0;
  border-top-right-radius: calc(var(--W) / 2);
  border-left: 1px solid rgba(255, 160, 220, 0.35);
}
.panel-left.panel-open {
  transform: translateX(-106%);
}
.panel-right.panel-open {
  transform: translateX(106%);
}

/* 门扇流光 */
.door-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 38%, rgba(255, 255, 255, 0.07) 50%, transparent 62%);
  background-size: 260% 100%;
  animation: sheen 7s ease-in-out infinite;
  pointer-events: none;
}
@keyframes sheen {
  0%, 55% { background-position: 120% 0; }
  85%, 100% { background-position: -120% 0; }
}

/* 门扇装饰点 */
.panel-dots {
  position: absolute;
  top: 12%;
  bottom: 12%;
  width: 26%;
  background-image: radial-gradient(circle at center, rgba(255, 190, 230, 0.85) 0 2px, transparent 2.6px);
  background-size: 30px 30px;
  opacity: 0.35;
}
.panel-left .panel-dots { left: 10%; }
.panel-right .panel-dots { right: 10%; }

/* 门扇符文 */
.p-rune {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 200, 235, 0.75);
  font-size: 22px;
  letter-spacing: 4px;
  writing-mode: vertical-rl;
  text-shadow: 0 0 12px rgba(255, 120, 200, 0.9), 0 0 28px rgba(123, 63, 242, 0.7);
  animation: runeGlow 3.2s ease-in-out infinite;
}
.p-rune.r1 { top: 14%; }
.p-rune.r2 { top: 44%; }
.p-rune.r3 { top: 72%; }
@keyframes runeGlow {
  0%, 100% { opacity: 0.55; text-shadow: 0 0 10px rgba(255, 120, 200, 0.7); }
  50% { opacity: 1; text-shadow: 0 0 20px rgba(255, 170, 230, 1), 0 0 42px rgba(123, 63, 242, 0.9); }
}

/* 门缝光 */
.door-seam {
  position: absolute;
  top: 3%;
  bottom: 3%;
  left: 50%;
  width: 3px;
  z-index: 3;
  transform: translateX(-50%);
  background: linear-gradient(180deg, transparent, rgba(255, 240, 250, 0.95) 18%, rgba(255, 160, 225, 0.85) 50%, rgba(140, 120, 255, 0.8) 82%, transparent);
  box-shadow: 0 0 18px rgba(255, 180, 230, 0.95), 0 0 42px rgba(150, 110, 255, 0.7);
  transition: opacity 0.8s ease 0.2s;
}
.seam-off {
  opacity: 0;
}

/* ============ 封印法阵 ============ */
.seal-array {
  position: absolute;
  left: 50%;
  top: 52%;
  width: 236px;
  height: 236px;
  transform: translate(-50%, -50%);
  z-index: 6;
  cursor: pointer;
  transition: transform 0.3s ease;
}
.seal-array:hover {
  transform: translate(-50%, -50%) scale(1.06);
}
.seal-array.breaking {
  pointer-events: none;
  animation: sealBreak 0.72s cubic-bezier(0.55, -0.2, 0.3, 1.5) forwards;
}
@keyframes sealBreak {
  0% { transform: translate(-50%, -50%) scale(1) rotate(0deg); opacity: 1; filter: brightness(1); }
  30% { transform: translate(-50%, -50%) scale(1.14) rotate(60deg); opacity: 1; filter: brightness(2.2); }
  100% { transform: translate(-50%, -50%) scale(0) rotate(220deg); opacity: 0; filter: brightness(3); }
}

.array-halo {
  position: absolute;
  inset: -26px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 130, 215, 0.3), rgba(123, 63, 242, 0.12) 50%, transparent 72%);
  animation: haloPulse 3s ease-in-out infinite;
}
@keyframes haloPulse {
  0%, 100% { transform: scale(1); opacity: 0.75; }
  50% { transform: scale(1.12); opacity: 1; }
}

.array-ring {
  position: absolute;
  border-radius: 50%;
}
.ar1 {
  inset: 4px;
  border: 1.5px dashed rgba(255, 138, 212, 0.85);
  animation: spin 26s linear infinite;
}
.ar2 {
  inset: 22px;
  border: 1px solid rgba(55, 216, 255, 0.55);
  border-style: dashed;
  animation: spinRev 18s linear infinite;
}
.ar3 {
  inset: 40px;
  border: 1px dashed rgba(255, 215, 107, 0.6);
  animation: spin 12s linear infinite;
}

.array-oct {
  position: absolute;
  inset: 52px;
  border: 1.5px solid rgba(255, 170, 225, 0.75);
  box-shadow: 0 0 14px rgba(255, 120, 200, 0.5), inset 0 0 14px rgba(255, 120, 200, 0.4);
  border-radius: 8px;
}
.a-oct1 {
  transform: rotate(45deg);
}
.a-oct2 {
  transform: rotate(0deg);
}

/* 刻度环 */
.array-ticks {
  position: absolute;
  inset: 68px;
  border-radius: 50%;
  background: repeating-conic-gradient(from 0deg, rgba(255, 235, 250, 0.95) 0deg 1.4deg, transparent 1.4deg 30deg);
  -webkit-mask-image: radial-gradient(circle, transparent 0 62%, #000 63% 72%, transparent 73%);
  mask-image: radial-gradient(circle, transparent 0 62%, #000 63% 72%, transparent 73%);
  animation: spin 40s linear infinite;
}

.array-core {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 42px;
  height: 42px;
  margin: -21px 0 0 -21px;
  transform: rotate(45deg);
  border-radius: 9px;
  background: linear-gradient(135deg, #fff, #ff8ad4 55%, #7b3ff2);
  box-shadow: 0 0 24px rgba(255, 138, 212, 1), 0 0 60px rgba(123, 63, 242, 0.8);
  animation: corePulse 2.2s ease-in-out infinite;
}
@keyframes corePulse {
  0%, 100% { transform: rotate(45deg) scale(1); }
  50% { transform: rotate(45deg) scale(1.18); }
}

.array-rune-wrap {
  position: absolute;
  inset: 0;
  animation: spin 60s linear infinite;
}
.array-rune {
  position: absolute;
  color: rgba(220, 240, 255, 0.9);
  font-size: 13px;
  text-shadow: 0 0 10px rgba(120, 210, 255, 0.95), 0 0 24px rgba(255, 130, 215, 0.7);
}

.array-hint {
  position: absolute;
  left: 50%;
  bottom: -44px;
  transform: translateX(-50%);
  white-space: nowrap;
  color: rgba(255, 200, 235, 0.92);
  font-size: 14px;
  letter-spacing: 3px;
  text-shadow: 0 0 12px rgba(255, 120, 200, 0.95);
  animation: hintPulse 2.4s ease-in-out infinite;
}
@keyframes hintPulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
@keyframes spinRev {
  to { transform: rotate(-360deg); }
}

/* 门标题 */
.gate-title {
  position: absolute;
  left: 50%;
  bottom: -74px;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: 16px;
  background: linear-gradient(90deg, #8eeaff, #ff8ad4 45%, #b78bff 70%, #ffd2ec);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 34px rgba(255, 140, 215, 0.45);
  animation: titleShimmer 4s linear infinite;
}
@keyframes titleShimmer {
  0% { background-position: 0% 0; }
  100% { background-position: 200% 0; }
}

/* ============ 阶段二：宇宙幻境 ============ */
.cosmos {
  position: absolute;
  inset: 0;
  z-index: 10;
  pointer-events: none;
}
.cosmos-in-enter-active {
  transition: opacity 1.1s ease 0.25s;
}
.cosmos-in-enter {
  opacity: 0;
}
.cosmos-in-enter-to {
  opacity: 1;
}

/* ---- 神秘之门 ---- */
/* ---- 神秘之门（位于罗盘时钟正中央） ---- */
.portal-aura {
  position: absolute;
  inset: -30%;
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(255, 120, 215, 0.38), rgba(123, 63, 242, 0.2) 46%, transparent 72%);
  animation: haloPulse 3.4s ease-in-out infinite;
}
.portal-ring {
  position: absolute;
  inset: 0;
  border-radius: 50% 50% 46% 46%;
}
.pr1 {
  border: 1.5px solid rgba(255, 170, 225, 0.6);
  box-shadow: 0 0 18px rgba(255, 130, 215, 0.45), inset 0 0 18px rgba(255, 130, 215, 0.25);
  animation: spin 16s linear infinite;
}
.pr2 {
  inset: 6%;
  border: 1px dashed rgba(120, 200, 255, 0.55);
  animation: spinRev 24s linear infinite;
}
.portal-swirl {
  position: absolute;
  inset: 16%;
  border-radius: 50%;
  background: conic-gradient(from 0deg, rgba(255, 111, 216, 0.9), rgba(123, 63, 242, 0.9), rgba(55, 216, 255, 0.9), rgba(255, 111, 216, 0.9));
  animation: spin 5s linear infinite;
  -webkit-mask-image: radial-gradient(circle, rgba(0, 0, 0, 0.96) 0 30%, transparent 66%);
  mask-image: radial-gradient(circle, rgba(0, 0, 0, 0.96) 0 30%, transparent 66%);
}
.portal-core {
  position: absolute;
  inset: 24%;
  border-radius: 50%;
  background: radial-gradient(circle, #fff 0%, #ffd2f0 20%, #ff6fd8 44%, #7b3ff2 66%, transparent 78%);
  box-shadow: 0 0 46px rgba(255, 111, 216, 0.85), inset 0 0 22px rgba(255, 255, 255, 0.55);
  animation: corePulse 2.8s ease-in-out infinite;
}
.portal-rune-wrap {
  position: absolute;
  inset: -20%;
  animation: spin 46s linear infinite;
}
.portal-rune {
  position: absolute;
  color: rgba(215, 240, 255, 0.85);
  font-size: 12px;
  text-shadow: 0 0 10px rgba(120, 210, 255, 0.95), 0 0 22px rgba(255, 130, 215, 0.75);
}

/* ---- 罗盘时钟（机械级联：外圈秒 → 内圈年） ---- */
.compass-clock {
  position: absolute;
  right: 3.5vw;
  top: 52%;
  transform: translateY(-50%);
  z-index: 14;
  pointer-events: none;
}

.clock-aura {
  position: absolute;
  inset: -60px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(123, 63, 242, 0.16), rgba(255, 111, 216, 0.1) 45%, transparent 70%);
  animation: haloPulse 4s ease-in-out infinite;
}

.clock-bob {
  position: absolute;
  inset: 0;
  animation: bob 7s ease-in-out infinite;
}

/* 菱形金色花纹罗盘背景（用户素材旋转45° + 透明底） */
.compass-frame {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: url('/compass-frame.png') center / contain no-repeat;
  opacity: 0.92;
  filter: drop-shadow(0 0 22px rgba(255, 150, 220, 0.35));
  pointer-events: none;
}
@keyframes bob {
  0%, 100% { transform: translateY(-7px); }
  50% { transform: translateY(7px); }
}

.clock-outer-ring {
  position: absolute;
  inset: 4px;
  border-radius: 50%;
  border: 1px dashed rgba(255, 180, 225, 0.4);
  animation: spin 90s linear infinite;
}
.clock-outer-ring::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 5px;
  height: 5px;
  margin: -2.5px 0 0 -2.5px;
  border-radius: 50%;
  background: #ff8ad4;
  box-shadow: 0 0 12px #ff8ad4;
}

/* 最外圈星座符号环：匀速顺时针旋转 */
.zodiac-ring {
  position: absolute;
  inset: 0;
  animation: spin 70s linear infinite;
  pointer-events: none;
}
.zodiac-sym {
  position: absolute;
  color: rgba(255, 218, 240, 0.75);
  text-shadow: 0 0 8px rgba(255, 130, 215, 0.8), 0 0 18px rgba(123, 63, 242, 0.5);
}

/* 细刻度环 */
.clock-ticks {
  position: absolute;
  inset: 3.2%;
  border-radius: 50%;
  background: repeating-conic-gradient(from 0deg, rgba(255, 220, 245, 0.4) 0deg 0.5deg, transparent 0.5deg 6deg);
  -webkit-mask-image: radial-gradient(circle, transparent 0 93%, #000 94% 96%, transparent 97%);
  mask-image: radial-gradient(circle, transparent 0 93%, #000 94% 96%, transparent 97%);
  opacity: 0.45;
}

/* 当前时间读取框（右半边中间水平位置，罩住年→秒全部刻度） */
.clock-readout {
  position: absolute;
  left: 65.5%;
  top: 50%;
  width: 29.5%;
  height: 6%;
  transform: translateY(-50%);
  border: 1px solid rgba(255, 170, 225, 0.55);
  border-radius: 10px;
  background: linear-gradient(90deg, rgba(123, 63, 242, 0.1), rgba(255, 111, 216, 0.16));
  box-shadow: 0 0 20px rgba(255, 130, 215, 0.28), inset 0 0 14px rgba(255, 130, 215, 0.12);
  z-index: 5;
}
.clock-readout::before,
.clock-readout::after {
  content: '';
  position: absolute;
  left: 50%;
  width: 7px;
  height: 7px;
  transform: translateX(-50%);
  background: rgba(255, 180, 230, 0.9);
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(255, 138, 212, 0.9);
}
.clock-readout::before { top: -4px; }
.clock-readout::after { bottom: -4px; }
.clock-readout-label {
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  letter-spacing: 3px;
  color: rgba(255, 214, 244, 0.85);
  text-shadow: 0 0 8px rgba(255, 130, 215, 0.9);
  white-space: nowrap;
}

/* 扫光 */
.clock-sheen {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(from 0deg, transparent 0 78%, rgba(255, 200, 240, 0.08) 88%, transparent 96%);
  animation: spin 14s linear infinite;
}

/* 文字圈：角度由 JS 级联驱动（秒圈每秒跳一格、内圈到点进位） */
.clock-ring {
  position: absolute;
  inset: 0;
  will-change: transform;
}
.clock-ring.step {
  transition: transform 0.8s cubic-bezier(0.45, 0, 0.2, 1);
}
.clock-ring.sec {
  transition: transform 0.45s cubic-bezier(0.3, 0, 0.2, 1);
}
.ring-item {
  position: absolute;
  left: 50%;
  top: 50%;
  color: rgba(255, 255, 255, 0.86);
  white-space: nowrap;
  text-shadow: 0 0 8px rgba(255, 150, 220, 0.9), 0 0 20px rgba(120, 150, 255, 0.6);
  letter-spacing: 1px;
}
@keyframes ringSpin {
  to { transform: rotate(360deg); }
}

/* 中央区域：神秘之门 */
.clock-center-zone {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 40%;
  height: 40%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 170, 225, 0.3);
  box-shadow: inset 0 0 30px rgba(123, 63, 242, 0.2), 0 0 26px rgba(255, 130, 215, 0.15);
}
.portal-inner {
  position: relative;
  pointer-events: auto;
  cursor: pointer;
}

/* ---- 文字 UI ---- */
.cosmos-ui {
  position: absolute;
  inset: 0;
  z-index: 16;
  pointer-events: none;
}
.cosmos-title {
  position: absolute;
  left: 4.5vw;
  top: 92px;
  animation: uiIn 1.4s ease 0.7s both;
}

.wenzi{
  position: absolute;
  left: 8vw;
  top: 230px;
  //width: 60vw;
  max-width: 840px;
}

/* 歌词滚动窗口：固定 5 行高度（5 × 60px），上下渐隐，超出部分裁掉 */
.wenzi-window {
  position: relative;
  height: 300px;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(180deg, transparent 0%, #000 18%, #000 82%, transparent 100%);
  mask-image: linear-gradient(180deg, transparent 0%, #000 18%, #000 82%, transparent 100%);
}

/* 中心光晕：行滚过窗口中间时被辉光点亮 */
.lyric-glow {
  position: absolute;
  left: 50%;
  top: 45%;
  width: 130%;
  height: 62px;
  transform: translate(-50%, -50%);
  background: radial-gradient(ellipse at center, rgba(255, 111, 216, 0.16), rgba(123, 63, 242, 0.08) 55%, transparent 75%);
  filter: blur(5px);
  pointer-events: none;
  z-index: 0;
}

/* 滚动轨道：整组内容向上平移 */
.wenzi-track {
  position: relative;
  z-index: 1;
  will-change: transform;
}

/* 每一行歌词：固定行高，最多两行，多余省略；底部细光分隔线 */
.wenzi-line {
  height: 60px;
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  -webkit-box-pack: center;
  line-height: 30px;
  overflow: hidden;
  color: #897191;
  font-size: 20px;
  text-align: left;
  text-shadow:
    0 0 12px rgba(137, 113, 145, 0.55),
    0 0 28px rgba(255, 111, 216, 0.16);
  border-bottom: 1px solid rgba(255, 170, 225, 0.1);
  opacity: 0.45;
  transition: opacity 0.6s ease;
}

/* 主唱行（窗口中间）：全亮 + 呼吸辉光 */
.wenzi-line.lyric-active {
  opacity: 1;
  animation: lyricBreathe 2.4s ease-in-out infinite;
}
@keyframes lyricBreathe {
  0%, 100% { text-shadow: 0 0 10px rgba(255, 138, 212, 0.3); }
  50% { text-shadow: 0 0 18px rgba(255, 138, 212, 0.55); }
}

/* 卡拉OK逐字：未点亮暗、点亮泛粉光 */
.lyric-ch {
  opacity: 0.22;
  transition: opacity 0.3s ease, color 0.3s ease, text-shadow 0.3s ease;
}
.lyric-ch.lit {
  opacity: 1;
  color: #ffd9ee;
  text-shadow: 0 0 8px rgba(255, 138, 212, 0.95), 0 0 22px rgba(255, 111, 216, 0.65);
}

/* 滚动光带：每次滚动时从底部向上扫过 */
.wenzi-window::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 60px;
  background: linear-gradient(180deg, transparent, rgba(255, 150, 220, 0.18) 45%, rgba(123, 63, 242, 0.12) 55%, transparent);
  opacity: 0;
  pointer-events: none;
  z-index: 3;
}
.wenzi-window.lyric-scrolling::after {
  animation: lyricSweep 1s ease-out;
}
@keyframes lyricSweep {
  0% { transform: translateY(300px); opacity: 0; }
  18% { opacity: 1; }
  82% { opacity: 1; }
  100% { transform: translateY(-70px); opacity: 0; }
}

/* 左侧流动时间轴：光点沿竖线从下往上流动 */
.lyric-rail {
  position: absolute;
  left: -22px;
  top: 6px;
  bottom: 6px;
  width: 2px;
  background: linear-gradient(180deg, transparent, rgba(255, 138, 212, 0.35) 20%, rgba(123, 63, 242, 0.35) 80%, transparent);
  border-radius: 2px;
  pointer-events: none;
  z-index: 2;
}
.rail-dot {
  position: absolute;
  left: 50%;
  top: 0;
  width: 6px;
  height: 6px;
  margin-left: -3px;
  border-radius: 50%;
  background: #ff8ad4;
  box-shadow: 0 0 10px #ff8ad4, 0 0 22px rgba(255, 138, 212, 0.85);
  animation: railFlow 7s linear infinite;
}
@keyframes railFlow {
  0% { top: -8px; opacity: 0; }
  12% { opacity: 1; }
  88% { opacity: 1; }
  100% { top: calc(100% + 8px); opacity: 0; }
}

/* 微光星星点缀 */
.lyric-star {
  position: absolute;
  border-radius: 50%;
  background: #ffffff;
  pointer-events: none;
  z-index: 2;
  animation: starTwinkle 4s ease-in-out infinite;
}
.lyric-star.s1 { left: 10%; top: 16%; width: 3px; height: 3px; box-shadow: 0 0 8px #ffd2f0, 0 0 16px rgba(255, 111, 216, 0.7); }
.lyric-star.s2 { right: 6%; top: 38%; width: 2px; height: 2px; box-shadow: 0 0 6px #8eeaff, 0 0 14px rgba(55, 216, 255, 0.6); animation-delay: 1.2s; }
.lyric-star.s3 { left: 3%; bottom: 18%; width: 2.5px; height: 2.5px; box-shadow: 0 0 7px #b78bff, 0 0 15px rgba(123, 63, 242, 0.65); animation-delay: 2.1s; }
.lyric-star.s4 { right: 16%; top: -6px; width: 2px; height: 2px; box-shadow: 0 0 5px #ffffff, 0 0 10px rgba(255, 255, 255, 0.7); animation: starFall 9s linear infinite; }
@keyframes starTwinkle {
  0%, 100% { opacity: 0.15; transform: scale(0.8); }
  50% { opacity: 0.9; transform: scale(1.25); }
}
@keyframes starFall {
  0% { transform: translateY(0); opacity: 0; }
  10% { opacity: 0.8; }
  90% { opacity: 0.6; }
  100% { transform: translateY(320px); opacity: 0; }
}




.cosmos-title h2 {
  margin: 0;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: 10px;
  background: linear-gradient(90deg, #8eeaff, #ff8ad4 45%, #b78bff 75%, #ffd2ec);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 0 22px rgba(255, 140, 215, 0.4));
}
.cosmos-title p {
  margin: 12px 0 0;
  color: rgba(215, 235, 255, 0.75);
  font-size: 14px;
  letter-spacing: 4px;
  text-shadow: 0 0 12px rgba(120, 180, 255, 0.7);
}
.cosmos-hint {
  position: absolute;
  left: 50%;
  bottom: 30px;
  transform: translateX(-50%);
  color: rgba(255, 220, 245, 0.8);
  font-size: 13px;
  letter-spacing: 4px;
  text-shadow: 0 0 12px rgba(255, 130, 215, 0.9);
  animation: uiIn 1.4s ease 1.2s both, hintPulse 3s ease-in-out 2.2s infinite;
}
@keyframes uiIn {
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
